import { SkipNavContent, SkipNavLink } from "@reach/skip-nav";
import CitationItemRecord from "models/view-models/citations/citation-item-record";
import TryLinkFooter from "molecules/footer/try-link-footer";
import NonsubscriberHeader from "molecules/header/unauthorized-header";
import CitationItemDisplay from "organisms/citation-item-display/citation-item-display";
import CitationsErrorNotice from "organisms/citations-error-notice/citations-error-notice";
import FullScreenTransition from "organisms/full-screen-transition/full-screen-transition";
import React from "react";
import { Redirect, useLocation } from "react-router";
import useCitationsToken from "utilities/hooks/domain/citations/use-citations-token";
import useCurrentIdentity from "utilities/hooks/use-current-identity";
import { t } from "utilities/localization-utils";

const CSS_BASE_CLASS = "c-citations-page";

const CITATION_TOKEN_PARAM_NAME = "token";

const CitationsPage = () => {
    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const token = params.get(CITATION_TOKEN_PARAM_NAME);
    const { currentIdentity } = useCurrentIdentity();
    const { citations, hasError, error, isLoading } = useCitationsToken(token);

    // Redirect to bookview if exactly one citation
    if (
        citations.length === 1 &&
        currentIdentity?.isConfiguredWithActiveSubscription()
    ) {
        const bookviewRedirectRoute = getRouteToFirstAndOnlyCitation(citations);
        if (bookviewRedirectRoute) {
            return <Redirect to={bookviewRedirectRoute} />;
        }
    }

    if (isLoading) {
        return (
            <FullScreenTransition
                transitionText={t("loadingItem", { item: t("citation") })}
            />
        );
    }

    return (
        <section className={CSS_BASE_CLASS}>
            <SkipNavLink>{t("skipToMainContent")}</SkipNavLink>
            <NonsubscriberHeader />
            <main className={`${CSS_BASE_CLASS}__main`}>
                <SkipNavContent className={`${CSS_BASE_CLASS}__content`}>
                    {hasError ? (
                        <CitationsErrorNotice errorKey={error} />
                    ) : (
                        <div className={`${CSS_BASE_CLASS}__citations`}>
                            {citations.map((citation, i) => (
                                <CitationItemDisplay
                                    key={citation.citation.externalId ?? i}
                                    bookviewRoute={citation.getRoute()}
                                    citationDisplayTitle={citation.getTitle()}
                                    publicationTitle={citation.getPublicationTitle()}
                                    body={citation.getBody()}
                                    collapse={i !== 0}
                                />
                            ))}
                        </div>
                    )}
                </SkipNavContent>
            </main>
            <footer className={`${CSS_BASE_CLASS}__footer`}>
                {!currentIdentity?.isConfiguredWithActiveSubscription() && (
                    <TryLinkFooter />
                )}
            </footer>
        </section>
    );
};

const getRouteToFirstAndOnlyCitation = (citations: CitationItemRecord[]) => {
    const citation = citations.at(0);

    return citation?.getRoute();
};

export default CitationsPage;
